import { createContext, useContext, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTour } from '@/components/onboarding/useTour';
import { useUser } from '@/contexts/UserContext';
import { APIRoutes } from '@/services/api.routes';
import type { Driver } from 'driver.js';

interface TourContextType {
  startTour: () => void;
  isTourActive: boolean;
  driverObj: Driver | null;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const TourProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateUser } = useUser();
  const tour = useTour(user?.featureConfig.communityMember ?? true);
  const previousTourActive = useRef(false);
  const hasAutoStarted = useRef(false);

  // Wrapper function to pass navigate to startTour
  const startTourWithNavigation = () => {
    tour.startTour(navigate);
  };

  // Mark onboarding as done when tour ends and redirect to courses
  useEffect(() => {
    // Tour was active and now it's not -> tour ended
    if (previousTourActive.current && !tour.isTourActive) {
      // Mark onboarding as complete
      APIRoutes.updateCurrentUser({
        frontendFlags: { onboardingDone: true }
      }).then((updatedUser) => {
        updateUser(updatedUser);
        // Redirect to courses after tour completion
        navigate('/courses/68fe076e277dd0f41198e589');
      }).catch((err) => {
        console.error('Failed to mark onboarding as done:', err);
      });
    }
    previousTourActive.current = tour.isTourActive;
  }, [tour.isTourActive, updateUser, navigate]);

  // Auto-start tour if onboardingDone is null and user is on dashboard
  useEffect(() => {
    if (
      user !== null &&
      user.frontendFlags.onboardingDone === null &&
      location.pathname === '/dashboard' &&
      !hasAutoStarted.current &&
      !tour.isTourActive
    ) {
      hasAutoStarted.current = true;
      // Small delay to ensure page is fully loaded
      setTimeout(() => {
        tour.startTour(navigate);
      }, 500);
    }
  }, [user, location.pathname, tour, navigate]);

  return (
    <TourContext.Provider value={{
      ...tour,
      startTour: startTourWithNavigation
    }}>
      {children}
    </TourContext.Provider>
  );
};

export const useTourContext = () => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTourContext must be used within TourProvider');
  }
  return context;
};
